import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MessageCircle } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <motion.div
      data-testid={`faq-item-${index}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: EASE }}
      className="border-b border-white/10 last:border-b-0"
    >
      <button
        data-testid={`faq-toggle-${index}`}
        type="button"
        onClick={() => onToggle(index)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-6 text-left transition-colors duration-300 hover:text-[#00F562]"
      >
        <span className="flex items-baseline gap-4">
          <span className="font-mono text-xs text-slate-500">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-display text-lg font-extrabold uppercase tracking-wide text-white sm:text-xl">
            {item.q}
          </span>
        </span>
        <span
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
            open
              ? "border-[#00F562]/60 bg-[#00F562]/10 text-[#00F562]"
              : "border-white/15 text-slate-300"
          }`}
        >
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            data-testid={`faq-answer-${index}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-3xl pb-6 pl-11 pr-6 text-base leading-relaxed text-slate-300 sm:text-[15px]">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Faq({ t, onContact }) {
  const [openIndex, setOpenIndex] = useState(0);
  const handleToggle = (i) => setOpenIndex(openIndex === i ? -1 : i);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative overflow-hidden py-24 lg:py-32"
    >
      <div className="absolute right-1/4 top-1/2 h-[40vh] w-[50vw] rounded-full bg-[#00F562]/[0.03] blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-[0.75fr_1.25fr] lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="lg:sticky lg:top-32 lg:self-start"
          >
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#00F562]">
              {t.faq.overline}
            </p>
            <h2
              data-testid="faq-title"
              className="mt-5 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl"
            >
              {t.faq.title}
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
              {t.faq.body}
            </p>
            <button
              data-testid="faq-contact-button"
              type="button"
              onClick={onContact}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-200 transition-all duration-300 hover:border-[#00F562]/60 hover:text-[#00F562]"
            >
              <MessageCircle size={13} />
              {t.chat.floatingLabel}
            </button>
          </motion.div>

          <div className="border-t border-white/10">
            {t.faq.items.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                index={i}
                open={openIndex === i}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
